// Time-stretch helpers (DRY/SOLID): centralize playbackRate and preservesPitch handling

export interface HasRole { role: string }

export function clampPlaybackRate(rate: number, min = 0.5, max = 2.0): number {
  return Math.max(min, Math.min(max, rate));
}

/** Canonical playback-rate stops shared across players (DAW-style: 0.5×–2×). */
export const PLAYBACK_RATE_STOPS: ReadonlyArray<number> = [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0] as const;

/** Single-element variant of {@link setPlaybackRateForAll} (no role registry). */
export function applyPlaybackRate(el: HTMLAudioElement | null | undefined, rate: number): void {
  if (!el) return;
  try { el.playbackRate = clampPlaybackRate(rate); } catch {}
}

/** Single-element variant of {@link setPreservesPitchForAll} (no role registry). */
export function applyPreservesPitch(el: HTMLAudioElement | null | undefined, preserve = true): void {
  if (!el) return;
  const a = el as any;
  try {
    if ('preservesPitch' in a) a.preservesPitch = preserve;
    if ('mozPreservesPitch' in a) a.mozPreservesPitch = preserve;
    if ('webkitPreservesPitch' in a) a.webkitPreservesPitch = preserve;
  } catch {}
}

/** Step `rate` to the next/prev stop in {@link PLAYBACK_RATE_STOPS}. */
export function stepPlaybackRate(rate: number, dir: 1 | -1): number {
  const stops = PLAYBACK_RATE_STOPS;
  // Find nearest index to current rate
  let idx = 0;
  let best = Infinity;
  for (let i = 0; i < stops.length; i++) {
    const d = Math.abs(stops[i] - rate);
    if (d < best) { best = d; idx = i; }
  }
  const next = Math.max(0, Math.min(stops.length - 1, idx + dir));
  return stops[next];
}

export function setPlaybackRateForAll<T extends HasRole>(
  elements: Record<string, HTMLAudioElement | null>,
  tracks: T[],
  rate: number
) {
  const r = clampPlaybackRate(rate);
  for (const t of tracks) {
    const el = elements[t.role];
    if (!el) continue;
    try { el.playbackRate = r; } catch {}
  }
}

export function setPreservesPitchForAll<T extends HasRole>(
  elements: Record<string, HTMLAudioElement | null>,
  tracks: T[],
  preservesPitch = true
) {
  for (const t of tracks) {
    const el = elements[t.role] as any;
    if (!el) continue;
    try {
      if ('preservesPitch' in el) el.preservesPitch = preservesPitch;
      if ('mozPreservesPitch' in el) el.mozPreservesPitch = preservesPitch;
      if ('webkitPreservesPitch' in el) el.webkitPreservesPitch = preservesPitch;
    } catch {}
  }
}

// Compute drift-corrected playbackRate factor for a follower track
export function computeDriftAdjustedRate(
  baseRate: number,
  errSeconds: number,
  opts?: { maxAdjust?: number; kP?: number }
): number {
  const maxAdjust = opts?.maxAdjust ?? 0.03; // +/- 3%
  const kP = opts?.kP ?? 0.6;
  const corr = 1 - Math.max(-maxAdjust, Math.min(maxAdjust, errSeconds * kP));
  return clampPlaybackRate(baseRate * corr);
}
